import { useContext } from 'react';
import { AxiosInstance, AxiosResponse } from 'axios';

import { AuthState } from '@okta/okta-auth-js';
import { useOktaAuth } from '@okta/okta-react';

import { StaleSessionContext } from '../app/StaleSessionContext/StaleSessionContext';
import { config } from '../config/env';
import { securityWorker } from '../models/Security';

/**
 * Base hook for creating authenticated API clients
 * Centralizes authentication logic and configuration
 */
export const useBaseApiClient = <T>(
  ClientClass: new (config: { securityWorker: any; baseURL: string }) => T
): T => {
  const { oktaAuth } = useOktaAuth();
  const { markSessionStale } = useContext(StaleSessionContext);

  const client = new ClientClass({
    securityWorker,
    baseURL: config.api.baseUrl,
  });

  const clientWithAuth = client as T & {
    setSecurityData?: (authState: AuthState | null) => void;
    instance?: AxiosInstance;
  };

  if (clientWithAuth.setSecurityData) {
    if (oktaAuth.authStateManager.getAuthState()?.isAuthenticated) {
      clientWithAuth.setSecurityData(oktaAuth.authStateManager.getAuthState());
    }

    oktaAuth.authStateManager.subscribe((authState) => {
      clientWithAuth.setSecurityData(authState);
    });
  }

  if (clientWithAuth.instance) {
    clientWithAuth.instance.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error: unknown) => {
        if ((error as { response?: { status?: number } }).response?.status === 401) {
          markSessionStale();
        }
        return Promise.reject(error);
      }
    );
  }

  return client;
};
